import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './client';
import { bagKeys } from './bag';
import { walletKeys } from './wallet';
import { journalKeys } from './journal';
import { collectionsKeys } from './collections';
import { freebieKeys } from './freebie';
import { scheduleRewardRefetch } from './rewardRefetch';
import { markAcquired } from '../bag/bagCache';
import { requestFlyer, disarmFlyer } from '../shell/flyerQueue';

// Item arrival (task-7-brief.md, docs/DESIGN.md §21): both forage and the daily land freebie
// share `DropDto`'s own item-XOR-currency contract (this file's own comment above `DropDto`) —
// a currency drop has a null `itemCode`, so this is what both `onSuccess` handlers below filter
// through before handing codes to `bagCache.markAcquired`.
const grantedItemCodes = (drops: DropDto[]): string[] =>
  drops.map((d) => d.itemCode).filter((code): code is string => code != null);

// The coin flyer's delta for a GAIN (docs/DESIGN.md §21): THIS GRANT'S OWN AMOUNT, never the
// wallet's net movement — the same rule `shops.ts`'s `usePurchase` was corrected to on 2026-09-05
// after a live run printed `+135` on a 15-Shell purchase, because two unrelated async Phase 3
// rewards landed inside the same window. §11.3: a token has to explain the thing that launched it.
// Nothing to correct on this side — these two have always summed the drops — but stated explicitly
// so a future pass "unifying" the three grant paths unifies them onto the right number. It also
// happens to be the only figure available here: neither mutation gets a wallet back in its
// response (both invalidate `walletKeys` instead), so there is no before/after balance to subtract
// even if one wanted to. The drops are the server's own authoritative statement of what it granted,
// and the item-XOR-currency contract above is what makes reading them unambiguous: a currency drop
// carries `currency`/`amount` and a null `itemCode`. Only Shells are summed, because the Shells
// chip is where the flyer lands; a `lantern_sparks` drop moves a different chip and gets no flight
// rather than a mislabelled one.
const shellsGained = (drops: DropDto[]): number =>
  drops.reduce((sum, d) => (d.currency === 'shells' ? sum + (d.amount ?? 0) : sum), 0);

// Phase 3 Task 11 widening (world/ActivityDto.java): normX/normY position the activity's hotspot
// on the land's map image; enabled mirrors the activities.enabled column directly — GET /world now
// includes disabled activities too (future placeholder hotspots can ship content-disabled and
// still be visible in the graph), so the client must check this flag itself rather than assume
// every returned activity is live.
export type WorldActivityDto = {
  code: string;
  activityType: string;
  name: string;
  normX: number;
  normY: number;
  enabled: boolean;
  // Only set for forage_node activities on cooldown; null once ready (or for non-forage
  // activity types, which never have a cooldown of their own).
  // Phase 4 Task 16 widening (server: world/ActivityDto.java): activity_type === 'arena' only —
  // config_json.pvp, distinguishing a PvP-capable venue (currently only emberfall_embercircle)
  // from an ordinary PvE-only arena. null/absent for every non-arena activity, where the question
  // doesn't apply. Optional (not just nullable), same convention bag.ts's own slot/levelReq
  // widening (Phase 4 Task 15) already established — every OTHER WorldActivityDto fixture in this
  // codebase (WorldScreen/LandScreen tests, gallery-entries) predates this widening and would
  // otherwise need updating just to keep compiling for a field nothing but BattleVenueSheet reads.
  pvp?: boolean | null;
  // Ability-slots-and-training-schools Task 10 fix-wave widening (server: world/ActivityDto.java's
  // `config`): the activity's own `config_json`, generically, for EVERY activity type — root-cause
  // fix for a real gap `pvp` above only patched narrowly for arena. This is what makes a training
  // hotspot's `config.element` (and a minigame hotspot's `config.game`) actually reach the client;
  // before this widening `hotspotAction` in `hotspotMeta.ts` had nothing real to read either from,
  // and the whole Abilities tab was an unreachable dead end (a real review finding — see that
  // file's own doc comments and `docs/RULINGS.md`). Untyped on purpose (`Record<string, unknown>`,
  // not a per-activityType shape) — this DTO doesn't know or care what a given `activityType`
  // authors into its config; each `hotspotAction` case narrows the one key it needs itself, the
  // same "content is data" split the server's own `pvpFrom`/`configFrom` already keep. Optional/
  // nullable for the same reason `pvp` is: every pre-existing `WorldActivityDto` fixture in this
  // codebase predates this field.
  config?: Record<string, unknown> | null;
  // Task 8 widening (server: world/ActivityStatusDto.java, riding `ActivityDto.status`) — "is
  // something waiting here" for THIS viewer: null for every activity nothing is waiting at,
  // including every activityType this feature doesn't cover. `count` is only set for
  // `plays_left` (remaining rewarded plays) and `quest_step` (the step number); null for the
  // other two kinds, which need no number. See `world/placeStatus.ts` for the copy this drives.
  status?: ActivityStatusDto | null;
};

export type ActivityStatusDto = {
  kind: 'gift_ready' | 'plays_left' | 'quest_step' | 'quest_new';
  count: number | null;
};

// Phase 3 Task 11 widening (world/WorldNodeDto.java): recommendedLevel is parsed from
// unlock_requirements_json's synthesized level gate (null if the land has no level gate);
// gateNote/description are copied straight off the world_nodes row (both nullable columns).
export type WorldNodeDto = {
  code: string;
  name: string;
  recommendedLevel: number | null;
  gateNote: string | null;
  description: string | null;
  activities: WorldActivityDto[];
};

// Server: world/WorldEdgeDto.java — one directed connection between two lands, by world_nodes
// code. Travel requirements aren't surfaced to the client yet, so this is deliberately just the
// two endpoints.
export type WorldEdgeDto = {
  from: string;
  to: string;
};

export type WorldDto = {
  nodes: WorldNodeDto[];
  edges: WorldEdgeDto[];
};

export const worldKeys = {
  all: ['world'] as const,
};

export const getWorld = () => apiFetch<WorldDto>('/world');

export function useWorld() {
  return useQuery({ queryKey: worldKeys.all, queryFn: getWorld });
}

// Forage lives alongside world (a forage activity is always read off a world node) rather than
// its own module — there's no forage.ts in the task-15 file list.
//
// Fix round 1 (review finding): a drop is an item OR a currency payout, never both — itemCode/
// name/rarity are null on a currency drop, currency/amount are null on an item drop, mirroring
// DropService's own item-XOR-currency contract server-side (each DropDto row is one or the
// other). `qty` stays non-null (an int, never nullable server-side) but is only meaningful on an
// item drop; the server hard-codes it to 0 for a currency drop (DropService.toResult,
// ForageService.grantResults) rather than sending 1, so treat it as ignorable on that branch.
export type DropDto = {
  itemCode: string | null;
  name: string | null;
  rarity: string | null;
  qty: number;
  currency: string | null;
  amount: number | null;
};

// The 180-minute forage PULL (`forage()` / `useForage`, `POST /forage/{code}`) was retired
// 2026-09-18 — RULINGS "Retiring the forage pull". Every forage pin is a timed gather session
// since the skill-craft polish (R1); `api/skills.ts` owns that mutation, and `api/forage.ts` the
// read-only board that survives on the same path.

// Phase 3 Task 11: fire-and-forget land entry (POST /world/{landCode}/visit → 204), for the
// quest/daily/badge triggers that key off a LandEntered domain event server-side. No response
// body to read, so nothing to write into any cache directly — but visiting can complete a daily
// task ("visit 3 lands" etc.), so the journal is invalidated on success.
export const visitLand = (landCode: string) =>
  apiFetch<void>(`/world/${landCode}/visit`, { method: 'POST', body: JSON.stringify({}) });

export function useVisitLand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (landCode: string) => visitLand(landCode),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: journalKeys.all }),
  });
}

// Server: world/FreebieResultDto.java — what a daily land freebie granted, plus when the per-land
// freebie next resets (the next UTC calendar-day boundary, a different clock than a forage node's
// nextReadyAt). Shares DropDto's own item-XOR-currency contract with ForageResultDto above.
export type FreebieResultDto = {
  drops: DropDto[];
  nextResetAt: string;
};

export const claimFreebie = (landCode: string) =>
  apiFetch<FreebieResultDto>(`/world/${landCode}/freebie`, { method: 'POST', body: JSON.stringify({}) });

// NOT optimistic — the server owns whether the freebie is actually claimable (409 freebie_claimed
// is a real, expected rejection a client can't preempt) and what got granted. Invalidates bag (an
// item drop landed there), wallet (a currency drop landed there), world (a claimed freebie may
// change what the land's activity card shows, e.g. a "come back tomorrow" state), and journal
// (claiming a freebie can complete a daily task).
export function useClaimFreebie(landCode: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => claimFreebie(landCode),
    onSuccess: (data) => {
      markAcquired(grantedItemCodes(data.drops));
      requestFlyer(shellsGained(data.drops));
      queryClient.invalidateQueries({ queryKey: bagKeys.all });
      queryClient.invalidateQueries({ queryKey: walletKeys.all });
      queryClient.invalidateQueries({ queryKey: worldKeys.all });
      queryClient.invalidateQueries({ queryKey: journalKeys.all });
      // See usePurchase's own comment: an item grant is what completes a collection set.
      queryClient.invalidateQueries({ queryKey: collectionsKeys.all });
      // The basket's own state: claimedToday flips, the board gains a found cell, and the set
      // progress moves — all three in one payload (docs/SCREENS.md "Welcome Basket"). Without
      // this the sheet would keep rendering the pre-claim board it opened with.
      queryClient.invalidateQueries({ queryKey: freebieKeys.all });
      // A freebie claim can complete a daily/quest step, but that reward lands via the outbox
      // AFTER this response — the journal invalidation above is too early to see it.
      scheduleRewardRefetch(queryClient);
    },
    onError: () => disarmFlyer(),
  });
}
